import type {
  PathwayFamily,
  PathwayFamilyDefault,
  PathwayFamilyList,
  PathwayFamilyVariant,
  PathwaySelection,
  PathwaySelectionPolicy,
  PathwayStep,
} from "@/api/library";

/**
 * The pure projection of the Pathway family contract (#642) into the shapes
 * #643's surfaces render.
 *
 * ## What this module may know, and what it must not
 *
 * It may REORDER nothing and DERIVE nothing clinical. Every ordering it returns
 * is the server's own `order`, every selection it reports is the server's own
 * `PathwaySelection`, and the maximum-Dynamic bound arrives as an argument read
 * from `pathwaySelection.maxDynamic` rather than as a constant. That last part
 * is the whole reason `dynamicCapacity` takes a policy instead of a literal
 * three: the ratified contract's "at most three active `Dynamic` variants" is
 * enforced by `adoption_preflight` against `MAX_DYNAMIC_PATHWAYS`, and a second
 * copy of the number in the browser is free to disagree with the one that
 * actually refuses the write. The parity audit already recorded that exact
 * defect on the reference product — PW-PRJ-10, a control that "declares max 2
 * but persists 3".
 *
 * It also may not decide FAMILY MEMBERSHIP. `parentPathwayId` is a stored
 * foreign key the service reports; nothing here groups by name, by placement
 * coordinate, or by any similarity between two assets.
 */

/**
 * How a family stands in the catalog, as the SERVICE's `archivedAt` fields say.
 *
 * Three states rather than two, because the service returns a fourth shape the
 * other two cannot describe. With archived rows hidden, `list_pathway_families`
 * still returns an archived DEFAULT when it owns an active variant — "omitting
 * it would turn that variant into an apparently parentless row and contradict
 * the family contract". Calling that family plainly `archived` would tell a
 * clinician their live variant is archived; calling it plainly `active` would
 * hide that its parent can no longer take a new version. `parent-archived` is
 * the fact, and it is the one a surface can act on: the variants are editable,
 * the default is not.
 */
export type PathwayFamilyState = "active" | "parent-archived" | "archived";

export function pathwayFamilyState(family: PathwayFamily): PathwayFamilyState {
  const parentArchived = family.default.archivedAt !== null;
  const anyActiveVariant = family.variants.some(
    (variant) => variant.archivedAt === null,
  );
  if (!parentArchived) {
    return "active";
  }
  return anyActiveVariant ? "parent-archived" : "archived";
}

/** One family member, tagged so a caller never has to test for a field. */
export type PathwayMember =
  | { readonly role: "default"; readonly asset: PathwayFamilyDefault }
  | { readonly role: "variant"; readonly asset: PathwayFamilyVariant };

/**
 * A family's default and its variants as one ordered list.
 *
 * The default FIRST, always — it is the parent, and the contract requires its
 * pin before any variant's. The variants keep the order the service sent them
 * in (`name_key`, then id), because catalog order is nonclinical presentation
 * and re-sorting it here would be this module inventing one.
 */
export function pathwayMembers(family: PathwayFamily): PathwayMember[] {
  return [
    { role: "default" as const, asset: family.default },
    ...family.variants.map(
      (asset) => ({ role: "variant" as const, asset }),
    ),
  ];
}

/**
 * Every member of every family, flattened — the join a per-asset lookup needs.
 *
 * Used to answer "which family does THIS asset belong to" from an asset id,
 * which is the question the asset page asks. Keyed on the id the service
 * reports, never on a name.
 */
export function pathwayFamilyOfAsset(
  list: PathwayFamilyList | undefined,
  assetId: string,
): PathwayFamily | null {
  return (
    list?.items.find(
      (family) =>
        family.default.assetId === assetId ||
        family.variants.some((variant) => variant.assetId === assetId),
    ) ?? null
  );
}

/**
 * The steps of one member, in the server's clinical order.
 *
 * `order` is "clinically meaningful zero-based order in this immutable
 * version", so it is SORTED ON rather than assumed to arrive sorted: the
 * contract states the field's meaning, not the array's arrangement, and a
 * surface that relied on arrival order would silently start showing a
 * different clinical sequence if the service ever grouped its rows.
 *
 * A copy, never the argument: callers render this and must not be able to
 * mutate a response held in the query cache.
 */
export function orderedSteps(steps: readonly PathwayStep[]): PathwayStep[] {
  return [...steps].sort((left, right) => left.order - right.order);
}

/**
 * How many steps a clinician can author here, and how many are engine-owned.
 *
 * Takes anything carrying a `kind` and an optional `editability`, so the catalog
 * can count the contract's `PathwayStep[]` and the editor can count an open
 * draft's rows without either converting into the other's shape.
 *
 * THE SERVICE DECIDES WHICH STEPS ARE AUTHORABLE, not this function, and #911 is
 * why that distinction now has teeth: two structured roles publish a typed
 * schema and the rest do not, so "structured means engine-owned" stopped being
 * true. A step is engine-owned exactly when the SERVER says
 * `editability: "protected"`. A draft row carries no `editability` — it is
 * `namedParts`' projection, whose only vocabulary is the part kind — so it keeps
 * the old reading, which is correct for it: an open draft's structured rows are
 * the ones #912's form will replace, and until then they are not authored here
 * either.
 */
export function stepCounts(
  steps: readonly { kind: string; editability?: string }[],
): {
  editable: number;
  protected: number;
} {
  let owned = 0;
  for (const step of steps) {
    const engineOwned =
      step.editability === undefined
        ? step.kind !== "prompt_text"
        : step.editability === "protected";
    if (engineOwned) {
      owned += 1;
    }
  }
  return { editable: steps.length - owned, protected: owned };
}

/**
 * The product name of one step's role.
 *
 * Deliberately NOT `namedParts.roleProductName`: that map is keyed
 * `<assetType>.<role>` and carries copy for the one role whose product name is
 * evidenced. A Pathway's roles — `screening_prompt`, `full_evaluation_steps` —
 * have no product name in any authority this repository holds, so humanising
 * the service's own vocabulary is the honest label and inventing one would be a
 * claim. This re-exports the same humanisation rather than a second rule.
 */
export { humaniseRole as pathwayStepLabel } from "./namedParts";

/**
 * The server's Always/Dynamic state, as one sentence a control can announce.
 *
 * `mode` and `order` are both the SERVER's — `order` "comes from the effective
 * `dynamic_track_slots` / `track_priority` / `dynamic_domains` registration,
 * never request order or a display name" — so the label states the position it
 * was given rather than a position this list computed. `null` selection is a
 * real answer and gets its own sentence: the contract says it means a member
 * that is "summary-only rather than an active selection", which is not the same
 * as a member whose selection failed to load.
 */
export function pathwaySelectionLabel(
  selection: PathwaySelection | null | undefined,
): string {
  if (!selection) {
    return "Not an active runtime selection";
  }
  return selection.mode === "always"
    ? `Always assessed · position ${selection.order + 1}`
    : `Dynamic · position ${selection.order + 1}`;
}

/**
 * How much of the server's Dynamic allowance this project's reported selections
 * consume.
 *
 * COUNTED FROM WHAT THE SERVER REPORTED, never from what the browser thinks is
 * adoptable: the input is the persisted `PathwaySelection` on each pin, which
 * preflight derived and persistence returned. So this is a rendering of the
 * server's own arithmetic, not a client-side re-run of its policy — the
 * difference the ticket's "without inventing a second frontend rule" names.
 *
 * `null` when the policy could not be read. A surface that cannot source the
 * cap must not show one; the server's refusal is unaffected by this read.
 */
export type DynamicCapacity = {
  /** Dynamic selections the server reports on this project's pins. */
  readonly used: number;
  /** `pathwaySelection.maxDynamic`, exactly as published. */
  readonly max: number;
  /** Never negative: a server that already holds more says `0` free, not `-1`. */
  readonly remaining: number;
  readonly atCapacity: boolean;
  /**
   * Whether the reported count EXCEEDS the published cap.
   *
   * Not a client verdict on the project — it is a disagreement between two
   * server answers read at different moments (a registry version that lowered
   * the cap, a pin set persisted under the old one). Surfaced rather than
   * clamped away, because "4 of 3" is the state an operator needs to see.
   */
  readonly overCapacity: boolean;
};

export function dynamicCapacity(
  selections: readonly (PathwaySelection | null | undefined)[],
  policy: PathwaySelectionPolicy | undefined,
): DynamicCapacity | null {
  if (policy === undefined) {
    return null;
  }
  const used = selections.filter(
    (selection) => selection?.mode === "dynamic",
  ).length;
  return {
    used,
    max: policy.maxDynamic,
    remaining: Math.max(0, policy.maxDynamic - used),
    atCapacity: used >= policy.maxDynamic,
    overCapacity: used > policy.maxDynamic,
  };
}
